<template>
  <div>
    <HrPageLayout
      title="My Meetings"
      subtitle="View and respond to your meetings"
      icon="📅"
      :breadcrumb-items="[
        { label: 'Dashboard', route: '/employee-dashboard' },
        { label: 'My Meetings' }
      ]"
    >
      <template #header-actions>
        <button class="hr-btn hr-btn-primary" @click="loadMeetings">
          <i class="bx bx-refresh"></i> Refresh
        </button>
      </template>

      <div class="my-meetings-content">
        <div class="hr-metrics-grid hr-mb-4">
          <div class="hr-metric-card gradient-1">
            <div class="hr-metric-icon"><i class="bx bx-calendar"></i></div>
            <p class="hr-metric-label">Total</p>
            <h2 class="hr-metric-value">{{ metrics.totalMeetings || 0 }}</h2>
          </div>
          <div class="hr-metric-card gradient-2">
            <div class="hr-metric-icon"><i class="bx bx-calendar-week"></i></div>
            <p class="hr-metric-label">Upcoming (7 days)</p>
            <h2 class="hr-metric-value">{{ metrics.upcomingWeek || 0 }}</h2>
          </div>
          <div class="hr-metric-card gradient-3">
            <div class="hr-metric-icon"><i class="bx bx-calendar-check"></i></div>
            <p class="hr-metric-label">Today</p>
            <h2 class="hr-metric-value">{{ metrics.todayMeetings || 0 }}</h2>
          </div>
          <div class="hr-metric-card gradient-4">
            <div class="hr-metric-icon"><i class="bx bx-time-five"></i></div>
            <p class="hr-metric-label">Pending response</p>
            <h2 class="hr-metric-value">{{ metrics.pendingResponses || 0 }}</h2>
          </div>
        </div>

        <div class="hr-chart-card hr-mb-4">
          <div class="hr-chart-body hr-py-3">
            <div class="meet-filters">
              <div class="hr-filter-group">
                <label class="hr-form-label">From</label>
                <input v-model="filters.startDate" type="date" class="hr-input" @change="loadMeetings">
              </div>
              <div class="hr-filter-group">
                <label class="hr-form-label">To</label>
                <input v-model="filters.endDate" type="date" class="hr-input" @change="loadMeetings">
              </div>
              <div class="hr-filter-group">
                <label class="hr-form-label">Status</label>
                <select v-model="filters.status" class="hr-select" @change="loadMeetings">
                  <option value="">All</option>
                  <option value="Scheduled">Scheduled</option>
                  <option value="Completed">Completed</option>
                  <option value="Canceled">Canceled</option>
                </select>
              </div>
              <button class="hr-btn hr-btn-primary" @click="loadMeetings">
                <i class="bx bx-refresh"></i> Refresh
              </button>
            </div>
          </div>
        </div>

        <div v-if="loading" class="hr-loading-wrap hr-py-5">
          <div class="hr-spinner"></div>
          <p class="hr-text-muted hr-mt-2">Loading your meetings...</p>
        </div>

        <div v-else-if="!meetings.length" class="hr-chart-card hr-text-center hr-py-5">
          <i class="bx bx-calendar" style="font-size: 48px; color: #3b82f6;"></i>
          <h5 class="hr-mt-4">No meetings found</h5>
          <p class="hr-text-muted">You have no meetings in the selected period.</p>
        </div>

        <div v-else class="hr-chart-card">
          <div class="hr-chart-body">
            <div class="hr-table-wrapper">
              <table class="hr-table hr-table-hover">
                <thead>
                  <tr>
                    <th>Meeting</th>
                    <th>Date & time</th>
                    <th>Location</th>
                    <th>Your response</th>
                    <th class="hr-text-end">Action</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="meeting in meetings" :key="meeting.id">
                    <td>
                      <strong>{{ meeting.title }}</strong>
                      <div class="hr-text-muted hr-small">{{ meeting.meetingType || 'Meeting' }} · {{ meeting.organizer || '' }}</div>
                    </td>
                    <td>
                      {{ meeting.formattedDate || meeting.startDate }}
                      <span v-if="meeting.formattedStartTime"> · {{ meeting.formattedStartTime }}</span>
                    </td>
                    <td>
                      <span>{{ meeting.location || '—' }}</span>
                      <a
                        v-if="hasJoinLink(meeting)"
                        :href="meetingJoinUrl(meeting)"
                        target="_blank"
                        rel="noopener noreferrer"
                        class="join-link"
                      >
                        Join
                      </a>
                    </td>
                    <td>
                      <span class="hr-badge" :class="getResponseBadgeClass(meeting.myResponse)">{{ meeting.myResponse || 'Pending' }}</span>
                    </td>
                    <td class="hr-text-end">
                      <template v-if="canRespondToMeeting(meeting)">
                        <button type="button" @click.stop="respond(meeting.id, 'Accepted')" class="hr-btn hr-btn-sm hr-btn-success hr-mr-1" :disabled="respondingId === meeting.id">Accept</button>
                        <button type="button" @click.stop="respond(meeting.id, 'Declined')" class="hr-btn hr-btn-sm hr-btn-danger hr-mr-1" :disabled="respondingId === meeting.id">Decline</button>
                        <button type="button" @click.stop="respond(meeting.id, 'Tentative')" class="hr-btn hr-btn-sm hr-btn-warning" :disabled="respondingId === meeting.id">Tentative</button>
                      </template>
                      <span v-else class="hr-text-muted hr-small">—</span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div v-if="pagination.totalPages > 1" class="hr-pagination-wrap hr-mt-4">
              <div class="hr-pagination-info">Page {{ pagination.currentPage }} of {{ pagination.totalPages }}</div>
              <nav class="hr-pagination">
                <button type="button" class="hr-page-btn" :disabled="pagination.currentPage <= 1"
                        @click="changePage(pagination.currentPage - 1)">
                  <i class="bx bx-chevron-left"></i>
                </button>
                <button v-for="page in visiblePages" :key="page" type="button" class="hr-page-btn"
                        :class="{ 'hr-page-active': pagination.currentPage === page }" @click="changePage(page)">{{ page }}</button>
                <button type="button" class="hr-page-btn" :disabled="pagination.currentPage >= pagination.totalPages"
                        @click="changePage(pagination.currentPage + 1)">
                  <i class="bx bx-chevron-right"></i>
                </button>
              </nav>
            </div>
          </div>
        </div>
      </div>
    </HrPageLayout>
  </div>
</template>

<script>
import HrPageLayout from "@/components/hrActions/HrPageLayout.vue";
import { authUrl, execute, toast } from "@/api";

export default {
  name: "MyMeetings",
  components: { HrPageLayout },
  data() {
    const start = new Date();
    start.setDate(start.getDate() - 30);
    const nextMonth = new Date();
    nextMonth.setDate(nextMonth.getDate() + 30);
    return {
      loading: true,
      respondingId: null,
      meetings: [],
      metrics: {},
      pagination: { currentPage: 1, totalPages: 1, totalMeetings: 0, limit: 10 },
      filters: {
        startDate: start.toISOString().split("T")[0],
        endDate: nextMonth.toISOString().split("T")[0],
        status: ""
      }
    };
  },
  computed: {
    visiblePages() {
      const total = this.pagination.totalPages;
      if (total <= 1) return [];
      const current = this.pagination.currentPage;
      const maxVisible = 5;
      let start = Math.max(1, current - Math.floor(maxVisible / 2));
      let end = Math.min(total, start + maxVisible - 1);
      if (end - start < maxVisible - 1) start = Math.max(1, end - maxVisible + 1);
      const pages = [];
      for (let i = start; i <= end; i++) pages.push(i);
      return pages;
    }
  },
  mounted() {
    this.clearStuckModalOverlay();
    this.loadMeetings();
  },
  beforeUnmount() {
    this.clearStuckModalOverlay();
  },
  methods: {
    clearStuckModalOverlay() {
      document.body.classList.remove("modal-open");
      document.body.style.removeProperty("overflow");
      document.body.style.removeProperty("padding-right");
      document.querySelectorAll(".modal-backdrop").forEach((el) => el.remove());
    },
    canRespondToMeeting(meeting) {
      if (!meeting || meeting.status !== "Scheduled") return false;
      const r = (meeting.myResponse ?? "").toString().trim().toLowerCase();
      return !r || r === "pending";
    },
    loadMeetings() {
      this.loading = true;
      const data = new FormData();
      data.append("function", "getMyMeetings");
      data.append("startDate", this.filters.startDate);
      data.append("endDate", this.filters.endDate);
      data.append("page", this.pagination.currentPage);
      data.append("limit", this.pagination.limit);
      if (this.filters.status) data.append("status", this.filters.status);
      execute(data, authUrl)
        .then((res) => {
          this.loading = false;
          if (res.data.success) {
            this.meetings = res.data.data.meetings || [];
            this.metrics = res.data.data.metrics || {};
            this.pagination = res.data.data.pagination || this.pagination;
          } else {
            toast.error(res.data.message || "Failed to load meetings");
            this.meetings = [];
          }
        })
        .catch((err) => {
          this.loading = false;
          toast.error(err.message || "Failed to load meetings");
          this.meetings = [];
        });
    },
    changePage(page) {
      if (page < 1 || page > this.pagination.totalPages) return;
      this.pagination = { ...this.pagination, currentPage: page };
      this.loadMeetings();
    },
    getResponseBadgeClass(response) {
      const r = (response || "").toLowerCase();
      if (r === "accepted") return "hr-badge-success";
      if (r === "declined") return "hr-badge-danger";
      if (r === "tentative") return "hr-badge-warning";
      return "hr-badge-secondary";
    },
    hasJoinLink(meeting) {
      const raw = (meeting?.virtualMeetingLink || "").toString().trim();
      return !!raw;
    },
    meetingJoinUrl(meeting) {
      const raw = (meeting?.virtualMeetingLink || "").toString().trim();
      if (!raw) return "#";
      if (/^https?:\/\//i.test(raw)) return raw;
      return `https://${raw}`;
    },
    respond(meetingId, response) {
      this.respondingId = meetingId;
      const data = new FormData();
      data.append("function", "respondToMeeting");
      data.append("meetingId", meetingId);
      data.append("response", response);
      execute(data, authUrl)
        .then((res) => {
          this.respondingId = null;
          if (res.data.success) {
            toast.success("Response recorded");
            this.loadMeetings();
          } else {
            toast.error(res.data.message || "Failed to update response");
          }
        })
        .catch((err) => {
          this.respondingId = null;
          toast.error(err.message || "Failed to update response");
        });
    }
  }
};
</script>

<style scoped>
@import "@/assets/css/hr-page-shared.css";
.meet-filters { display: flex; flex-wrap: wrap; gap: 12px; align-items: flex-end; }
.hr-text-end { text-align: right; }
.hr-mr-1 { margin-right: 8px; }
</style>
